import { IconButton } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { Dataset } from "@/api";
import type { LoadState } from "@/components/dataset/loadState";
import { ChevronRightIcon } from "@/components/icons";
import { ChartCard } from "@/components/metric";
import { HatListView } from "@/components/table/HatListView";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { DatasetListRow } from "./DatasetListRow";

/**
 * 최근 등록 데이터셋 카드. 개요 페이지의 목록 카드다.
 *
 * 행을 클릭하면 상세 **창**을 연다(목록 행 클릭과 같은 관례). 필터 드릴다운이 아니므로
 * 페이지의 read-only 성격은 유지된다. 모집단이 available 뿐이라 상태 열은 두지 않는다.
 * 데이터셋을 구분하는 값(이름·task·담당자·embodiment·에피소드·총 길이·총 용량·등록일)만 적는다.
 *
 * 목록은 `HatListView` 다. 표 컬럼을 맞출 필요가 없는 5행 요약이라 이름을 큰 줄로 두고
 * 나머지는 메타 한 줄에 둔다. 카드 chrome(면·테두리)은 ChartCard 가 가진다.
 */
export function RecentDatasetsCard({
  state,
  onRetry,
  onThumbnailStale,
}: {
  state: LoadState<readonly Dataset[]>;
  onRetry: () => void;
  /** 섬네일 presigned URL 만료 신호. 페이지가 디바운서로 감싼 재조회 함수를 전달한다. */
  onThumbnailStale: () => void;
}): ReactNode {
  return (
    <ChartCard
      title="최근 등록"
      // 부제는 목록을 읽는 방법이다. 그래서 목록이 있을 때만 표시한다.
      subtitle={
        state.status === "success"
          ? "만들어진 순서의 최신 5건입니다. 행을 누르면 상세 창이 열립니다."
          : undefined
      }
      loading={
        state.status === "success" || state.status === "error"
          ? undefined
          : "최근 등록 Dataset을 불러오고 있습니다."
      }
      error={state.status === "error" ? { message: state.message, onRetry } : undefined}
      empty={
        state.status === "success" && state.data.length === 0
          ? "아직 등록된 Dataset이 없습니다."
          : undefined
      }
    >
      {state.status === "success" ? (
        <HatListView
          ariaLabel="최근 등록 Dataset"
          items={state.data}
          getKey={(dataset) => dataset.uuid}
          // 이 목록에는 선택할 것이 없다. 행 활성화가 곧 상세 창 열기라 선택 상태를 남기지 않고,
          // 화살표 탐색도 끈다(c6 ListView 는 selection-follows-focus 라 켜 두면 화살표 한 번에
          // 창이 열린다). 대신 행마다 액션 버튼이 있어 Tab 만으로 모든 행에 도달한다.
          selectedKey={null}
          keyboardNavigation={false}
          onSelect={openDetailWindow}
          // 행 콘텐츠와 액션은 한 descriptor 로 전달한다. c6 ListView 가 액션을 primary
          // 버튼의 형제 셀로 렌더링해 행 선택과 이벤트를 분리한다.
          renderItem={(dataset) => ({
            content: (
              <DatasetListRow dataset={dataset} mode="recent" onThumbnailError={onThumbnailStale} />
            ),
            actions: (
              <IconButton
                size="sm"
                variant="ghost"
                border={false}
                aria-label={`${dataset.display_name} 상세 창 열기`}
                onClick={() => openDetailWindow(dataset)}
              >
                <ChevronRightIcon />
              </IconButton>
            ),
          })}
        />
      ) : null}
    </ChartCard>
  );
}

function openDetailWindow(dataset: Dataset): void {
  openDatasetDetailWindow({ datasetUuid: dataset.uuid, displayName: dataset.display_name });
}
